import type { ChatAttachment } from '@src/api/shared/community/chatApiClient';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import logger from '@src/utils/logger';
import { IDBPDatabase, openDB } from 'idb';

export const DB_NAME = 'online_community';
export const DRAFT_STORE_NAME = 'chat_drafts';
export const THREAD_DRAFT_STORE_NAME = 'chat_thread_drafts';

export type Attachment = {
  name: string;
  type: string;
  size: number;
  blob: Blob;
  chatAttachment?: ChatAttachment;
};

export type Draft = {
  channelCode: string;
  message: string;
  attachments: Attachment[];
  updatedAt: number;
  userType?: ChannelMemberUserTypeEnum;
  userId?: number;
};

export type ThreadDraft = {
  channelCode: string;
  parentChatId: number;
  message: string;
  attachments: Attachment[];
  updatedAt: number;
  userType?: ChannelMemberUserTypeEnum;
  userId?: number;
};

const ENCRYPTION_KEY = import.meta.env.VITE_CHAT_DRAFT_SECRET_KEY;

if (!ENCRYPTION_KEY) {
  logger.error('Encryption key not found.');
}

export async function encryptData(data: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const encoded = encoder.encode(data);
    const key = await crypto.subtle.importKey(
      'raw',
      encoder.encode(ENCRYPTION_KEY),
      'AES-GCM',
      false,
      ['encrypt'],
    );
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
    const encryptedArray = new Uint8Array(encrypted);
    return btoa(
      JSON.stringify({
        iv: Array.from(iv),
        data: Array.from(encryptedArray),
      }),
    );
  } catch (error) {
    logger.error('Encryption failed, storing data unencrypted:', error);
    return btoa(data);
  }
}

export async function decryptData(encryptedBase64: string): Promise<string> {
  try {
    const { iv, data } = JSON.parse(atob(encryptedBase64));
    const key = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(ENCRYPTION_KEY),
      'AES-GCM',
      false,
      ['decrypt'],
    );
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: new Uint8Array(iv) },
      key,
      new Uint8Array(data),
    );
    return new TextDecoder().decode(decrypted);
  } catch (error) {
    logger.error('Decryption failed, attempting to read as unencrypted:', error);
    try {
      return atob(encryptedBase64);
    } catch {
      // base64でもない場合、そのまま返す
      return encryptedBase64;
    }
  }
}

export async function getDbPromise(): Promise<IDBPDatabase | null> {
  // SSR対応
  if (typeof window === 'undefined') {
    return null;
  }

  return await openDB(DB_NAME, 2, {
    upgrade(db, oldVersion) {
      // バージョン1: chat_draftsストアを作成
      if (oldVersion < 1) {
        db.createObjectStore(DRAFT_STORE_NAME, { keyPath: ['channelCode', 'userType', 'userId'] });
      }
      // バージョン2: chat_thread_draftsストアを作成
      if (oldVersion < 2) {
        db.createObjectStore(THREAD_DRAFT_STORE_NAME, {
          keyPath: ['channelCode', 'userType', 'userId', 'parentChatId'],
        });
      }
    },
  });
}
